import { Component, createRef, type ReactNode, type RefObject } from 'react';

/**
 * The error boundary around one component another feature put in a page's place (F1-d spec FR-S2:
 * a failing panel shows its error inside its own place). A component that throws is replaced by the
 * caller's fallback inside its own slot, so its siblings and the rest of the page keep working; the
 * error goes to `onError` (the app's telemetry seam), never to the console. Draws nothing of its own:
 * the fallback is the caller's, sized for where it sits.
 *
 * **Retry and focus** (WCAG 2.4.3). The fallback puts `retry` on its *Try again* and `retryRef` on
 * that button. Either outcome of a retry removes the button: a retry that renders replaces the
 * fallback with the component, and one that throws again mounts a new fallback. When focus was on the
 * button, or nowhere, at the press, it then moves — to `focusAfterRetry` (the place's wrapper, with
 * `tabIndex={-1}`) when the component rendered, or to the new fallback's *Try again* when it threw
 * again. Focus the user has put anywhere else is left where it is.
 *
 * A class, because an error boundary has no hook form (`src/app/AppErrorBoundary.tsx`).
 */
export interface PlaceErrorBoundaryProps {
    readonly children: ReactNode;
    /** Told of each error caught. */
    readonly onError?: ((error: unknown) => void) | undefined;
    /** The fallback: `retry` goes on its *Try again*, and `retryRef` is that button's `ref`. */
    readonly renderFallback: (retry: () => void, retryRef: RefObject<HTMLButtonElement | null>) => ReactNode;
    /** Where focus goes after a retry that renders the component — the place's wrapper, `tabIndex={-1}`. */
    readonly focusAfterRetry: RefObject<HTMLElement | null>;
}

interface PlaceErrorBoundaryState {
    readonly failed: boolean;
}

export class PlaceErrorBoundary extends Component<PlaceErrorBoundaryProps, PlaceErrorBoundaryState> {
    override state: PlaceErrorBoundaryState = { failed: false };

    private readonly retryRef = createRef<HTMLButtonElement>();

    /** Set by a retry pressed with focus on its button or nowhere; the next commit moves focus. */
    private repairFocus = false;

    static getDerivedStateFromError(): PlaceErrorBoundaryState {
        return { failed: true };
    }

    override componentDidCatch(error: unknown): void {
        this.props.onError?.(error);
    }

    override componentDidUpdate(): void {
        if (!this.repairFocus) {
            return;
        }
        this.repairFocus = false;
        const active = document.activeElement;
        if (active !== null && active !== document.body && active.isConnected) {
            return;
        }
        if (this.state.failed) {
            this.retryRef.current?.focus();
        } else {
            this.props.focusAfterRetry.current?.focus();
        }
    }

    private readonly retry = (): void => {
        const active = document.activeElement;
        this.repairFocus = active === null || active === document.body || active === this.retryRef.current;
        this.setState({ failed: false });
    };

    override render(): ReactNode {
        return this.state.failed ? this.props.renderFallback(this.retry, this.retryRef) : this.props.children;
    }
}
